/* ------------------------------------------------------------------ */
/* getProofImage                                                      */
/* CAMS /getImage returns the proof in one of two shapes:             */
/*   - fileUrl: "url1 | url2 ..."  -> array of URLs (rendered direct) */
/*   - image:   base64( [gzip]( file ) )  -> a single proof           */
/* The `image` payload is frequently GZIP-compressed (base64 begins   */
/* "H4sI"). The old code never decompressed and guessed the MIME from */
/* the base64 prefix, so it shipped gzip bytes mislabeled image/jpeg  */
/* and the browser showed a broken image. We now decode -> gunzip (if */
/* gzipped) -> sniff the REAL type from the decoded bytes -> rebuild  */
/* a correctly-labeled data: URI.                                     */
/* ------------------------------------------------------------------ */

const base64ToBytes = (b64) => {
  // Tolerate base64url (-/_), whitespace, and missing padding.
  let s = String(b64).replace(/\s/g, "").replace(/-/g, "+").replace(/_/g, "/");
  while (s.length % 4) s += "=";
  const bin = atob(s);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
};

const bytesToBase64 = (bytes) => {
  let bin = "";
  const CHUNK = 0x8000; // chunk to stay under String.fromCharCode arg limits
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return btoa(bin);
};

const gunzip = async (bytes) => {
  if (typeof DecompressionStream === "undefined") {
    // No native gzip support (very old browser). Surface it instead of
    // silently shipping compressed bytes; swap in `pako` here if needed.
    throw new Error("DecompressionStream unavailable — gzip proof cannot be decoded");
  }
  const ds = new DecompressionStream("gzip");
  const stream = new Blob([bytes]).stream().pipeThrough(ds);
  return new Uint8Array(await new Response(stream).arrayBuffer());
};

// Detect the true file type from the leading bytes (magic numbers).
const mimeFromBytes = (bytes) => {
  const h = Array.from(bytes.slice(0, 4))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  if (h.startsWith("25504446")) return "application/pdf"; // %PDF
  if (h.startsWith("ffd8ff")) return "image/jpeg"; // JPEG
  if (h.startsWith("89504e47")) return "image/png"; // PNG
  if (h.startsWith("47494638")) return "image/gif"; // GIF8
  if (h.startsWith("52494646")) return "image/webp"; // RIFF (WEBP)
  return null;
};

// base64( [gzip]( file ) ) -> correctly-labeled data: URI
const decodeImagePayload = async (base64Data) => {
  let bytes = base64ToBytes(base64Data);

  // gzip magic 0x1f 0x8b -> decompress to the real file bytes.
  if (bytes[0] === 0x1f && bytes[1] === 0x8b) {
    bytes = await gunzip(bytes);
  }

  const mime = mimeFromBytes(bytes);
  if (!mime) {
    // Real bytes but unrecognised signature — log the header so a bad
    // payload is visible rather than silently rendering broken.
    const head = Array.from(bytes.slice(0, 8))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join(" ");
    console.warn("getProofImage: unrecognised proof signature; header =", head);
  }

  return `data:${mime || "image/jpeg"};base64,${bytesToBase64(bytes)}`;
};

export const getProofImage = async (transactionId, eiano) => {
  const BaseUrl = process.env.REACT_APP_API_URL;
  const apiUrl = BaseUrl + "/riCustomerManagement/v1/getImage";

  const body = JSON.stringify({ transactionId, eiano });

  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body,
    });

    if (!response.ok) {
      console.error("Failed to fetch transaction image", response.status);
      return null;
    }

    const responseData = await response.json();

    // 0) FaceMatch — response carries TWO proofs: selfieImage (submitted
    //    photo) and image (reference). Each is base64( [gzip]( file ) ) and
    //    may be a JPEG/PNG *or* a PDF, so decode BOTH through the same path
    //    (gunzip -> sniff real MIME -> labeled data: URI). Returning the
    //    { selfieImage, image } object is what Viewproof's FaceMatch branch
    //    consumes.
    if (responseData.selfieImage) {
      const decodeSafe = async (payload) => {
        if (!payload) return "";
        try {
          return await decodeImagePayload(payload);
        } catch (e) {
          console.error("getProofImage: failed to decode FaceMatch payload", e);
          return "";
        }
      };
      const [selfieImage, image] = await Promise.all([
        decodeSafe(responseData.selfieImage),
        decodeSafe(responseData.image),
      ]);
      return { selfieImage, image };
    }

    // 1) Direct file URL(s) — pipe-separated. Prefer this when present.
    if (responseData.fileUrl) {
      return responseData.fileUrl
        .split("|")
        .map((url) => url.trim())
        .filter(Boolean);
    }

    // 2) Inline base64 (possibly gzip-compressed) proof.
    if (responseData.image) {
      try {
        return await decodeImagePayload(responseData.image);
      } catch (e) {
        console.error("getProofImage: failed to decode image payload", e);
        return null;
      }
    }

    console.error("Image data not found in the response");
    return null;
  } catch (error) {
    console.error("Error fetching transaction image:", error);
    return null;
  }
};